import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime, formatEuro } from "../../lib/format";
import { STATO_META } from "../../lib/proforma";
import { downloadProformaPdf } from "../../lib/proformaPdf";
import PageHeader from "../../components/PageHeader";
import ProformaDialog from "../../components/ProformaDialog";
import ProformaPreviewDialog from "../../components/ProformaPreviewDialog";
import EmailDialog from "../../components/EmailDialog";
import PecDialog from "../../components/PecDialog";
import { proformaPdfAttachment } from "../../lib/proformaPdf";
import { Button } from "../../components/ui/button";
import { FileText, Loader2, Plus, Trash2, Pencil, Eye, CheckCircle2, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function FattureProforma() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [studio, setStudio] = useState(null);
  const [busy, setBusy] = useState("");
  const [previewPf, setPreviewPf] = useState(null);
  const [emailState, setEmailState] = useState(null); // {attachments, prefillTo, prefillSubject}
  const [pecState, setPecState] = useState(null);
  const [tab, setTab] = useState("attive");
  const [trash, setTrash] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, me, trashRes] = await Promise.all([api.get("/studio/proforme"), api.get("/studio/me"), api.get("/studio/proforme/cestino")]);
      setRows(data || []);
      setStudio(me.data);
      setTrash(trashRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setDlgOpen(true); };
  const openEdit = (f) => { setEditing(f); setDlgOpen(true); };

  const del = async (f) => {
    if (!window.confirm(`Spostare la proforma n. ${f.numero} nel cestino?`)) return;
    try { await api.delete(`/studio/proforme/${f.id}`); toast.success("Spostata nel cestino"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const restore = async (f) => {
    try { await api.post(`/studio/proforme/${f.id}/ripristina`); toast.success("Fattura ripristinata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const hardDelete = async (f) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE la proforma n. ${f.numero}? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/studio/proforme/${f.id}/definitivo`); setTrash((x) => x.filter((i) => i.id !== f.id)); toast.success("Eliminata definitivamente"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStato = async (f, stato) => {
    try { await api.patch(`/studio/proforme/${f.id}`, { stato }); setRows((x) => x.map((i) => (i.id === f.id ? { ...i, stato } : i))); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (f) => {
    if (!window.confirm(`Convertire la proforma n. ${f.numero} in fattura definitiva? Verrà registrata in Contabilità.`)) return;
    setBusy(f.id);
    try {
      const { data } = await api.post(`/studio/proforme/${f.id}/converti`);
      toast.success(`Convertita in fattura n. ${data.fattura_numero}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const pdf = async (f) => {
    setBusy(f.id);
    try { await downloadProformaPdf(f, studio); }
    catch (e) { toast.error("Generazione PDF non riuscita"); }
    finally { setBusy(""); }
  };

  const sendVia = async (f, channel) => {
    setBusy(f.id);
    try {
      const att = await proformaPdfAttachment(f, studio);
      const cd = f.cliente_dati || {};
      const prefillTo = channel === "pec" ? (cd.pec || "") : (cd.email || "");
      const prefillSubject = `Fattura proforma n. ${f.numero}`;
      if (channel === "pec") setPecState({ attachments: [att], prefillTo, prefillSubject });
      else setEmailState({ attachments: [att], prefillTo, prefillSubject });
    } catch (e) { toast.error("Preparazione allegato non riuscita"); }
    finally { setBusy(""); }
  };

  const totNetto = rows.reduce((s, f) => s + Number(f.totale_netto || 0), 0);
  const totIncasso = rows.filter((f) => f.stato === "convertita").reduce((s, f) => s + Number(f.totale_netto || 0), 0);

  return (
    <div data-testid="proforme-page">
      <PageHeader
        title="Fatture Proforma"
        subtitle="Documenti proforma da professionista. Regime forfettario di default, altri regimi selezionabili."
        icon={FileText}
        actions={<Button onClick={openNew} data-testid="proforma-new-btn" className="bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold gap-1"><Plus className="h-4 w-4" /> Nuova Fattura Proforma</Button>}
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Proforma emesse</div>
          <div className="text-2xl font-head font-extrabold text-slate-50 mt-1" data-testid="proforma-count">{rows.length}</div>
        </div>
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale netto (tutte)</div>
          <div className="text-2xl font-head font-extrabold text-emerald-400 mt-1 font-mono" data-testid="proforma-tot">{formatEuro(totNetto)}</div>
        </div>
        <div className="pgp-card p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale incasso (convertite)</div>
          <div className="text-2xl font-head font-extrabold text-teal-400 mt-1 font-mono" data-testid="proforma-tot-incasso">{formatEuro(totIncasso)}</div>
        </div>
      </div>

      <div className="flex gap-2 mb-3" data-testid="proforma-tabs">
        <button onClick={() => setTab("attive")} data-testid="proforma-tab-attive" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === "attive" ? "bg-teal-500 text-[#0a0d14]" : "bg-muted text-foreground hover:bg-accent hover:text-accent-foreground"}`}>Attive ({rows.length})</button>
        <button onClick={() => setTab("cestino")} data-testid="proforma-tab-cestino" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === "cestino" ? "bg-teal-500 text-[#0a0d14]" : "bg-muted text-foreground hover:bg-accent hover:text-accent-foreground"}`}><Trash2 className="h-3.5 w-3.5" /> Cestino ({trash.length})</button>
      </div>

      <div className="pgp-card overflow-x-auto pgp-fade-up">
        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>
        ) : tab === "cestino" ? (
          trash.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="cestino-empty">Il cestino è vuoto.</div>
          ) : (
            <table className="w-full text-sm" data-testid="cestino-table">
              <thead>
                <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                  <th className="px-4 py-3">N.</th>
                  <th className="px-4 py-3">Intestatario</th>
                  <th className="px-4 py-3">Eliminata il</th>
                  <th className="px-4 py-3 text-right">Netto</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {trash.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`cestino-row-${f.id}`}>
                    <td className="px-4 py-3 font-mono text-slate-400">{f.numero}</td>
                    <td className="px-4 py-3 text-slate-100">{f.intestatario || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{formatDateTime(f.deleted_at)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-400">{formatEuro(f.totale_netto)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => restore(f)} data-testid={`cestino-restore-${f.id}`} title="Ripristina" className="text-slate-400 hover:text-emerald-400"><RotateCcw className="h-4 w-4" /></button>
                        <button onClick={() => hardDelete(f)} data-testid={`cestino-harddel-${f.id}`} title="Elimina definitivamente" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">Nessuna proforma. Crea la prima con "Nuova Fattura Proforma".</div>
        ) : (
          <table className="w-full text-sm" data-testid="proforme-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                <th className="px-4 py-3">N.</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Intestatario</th>
                <th className="px-4 py-3">Pratica</th>
                <th className="px-4 py-3">Regime</th>
                <th className="px-4 py-3 text-right">Netto</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((f) => {
                const sm = STATO_META[f.stato] || STATO_META.bozza;
                return (
                  <tr key={f.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`proforma-row-${f.id}`}>
                    <td className="px-4 py-3 font-mono text-teal-400">{f.numero}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{formatDateTime(f.created_at || f.data)}</td>
                    <td className="px-4 py-3 text-slate-100">{f.intestatario || "—"}</td>
                    <td className="px-4 py-3 font-mono text-slate-400 text-xs">{f.pratica_numero || "—"}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{f.regime_label || f.regime || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-400 font-semibold">{formatEuro(f.totale_netto)}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${sm.cls}`} data-testid={`proforma-stato-${f.id}`}>{sm.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {f.stato === "bozza" && (
                          <button onClick={() => setStato(f, "inviata")} data-testid={`proforma-send-${f.id}`} title="Segna come inviata" className="text-slate-400 hover:text-cyan-400"><Send className="h-4 w-4" /></button>
                        )}
                        <button onClick={() => setPreviewPf(f)} data-testid={`proforma-preview-${f.id}`} title="Anteprima / PDF / Invio" className="text-slate-400 hover:text-teal-400 disabled:opacity-40" disabled={busy === f.id}>
                          {busy === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button onClick={() => openEdit(f)} data-testid={`proforma-edit-${f.id}`} title="Modifica" className="text-slate-400 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
                        {f.stato !== "convertita" && (
                          <>
                            <button onClick={() => convert(f)} data-testid={`proforma-convert-${f.id}`} title="Converti in fattura" className="text-slate-400 hover:text-emerald-400"><CheckCircle2 className="h-4 w-4" /></button>
                          </>
                        )}
                        {f.stato === "convertita" && f.fattura_numero && (
                          <span className="text-[11px] text-emerald-400 font-mono" title="Fattura definitiva">→ {f.fattura_numero}</span>
                        )}
                        <button onClick={() => del(f)} data-testid={`proforma-del-${f.id}`} title="Elimina" className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ProformaDialog open={dlgOpen} onOpenChange={setDlgOpen} editing={editing} onSaved={(created) => { load(); if (created) setPreviewPf(created); }} />
      <ProformaPreviewDialog
        open={!!previewPf}
        onOpenChange={(o) => !o && setPreviewPf(null)}
        proforma={previewPf}
        studio={studio}
        onEmail={(f) => { setPreviewPf(null); sendVia(f, "email"); }}
        onPec={(f) => { setPreviewPf(null); sendVia(f, "pec"); }}
      />
      <EmailDialog
        open={!!emailState}
        onOpenChange={(o) => !o && setEmailState(null)}
        pratica={null}
        initialAttachments={emailState?.attachments}
        prefillTo={emailState?.prefillTo}
        prefillSubject={emailState?.prefillSubject}
      />
      <PecDialog
        open={!!pecState}
        onOpenChange={(o) => !o && setPecState(null)}
        pratica={null}
        initialAttachments={pecState?.attachments}
        prefillTo={pecState?.prefillTo}
        prefillSubject={pecState?.prefillSubject}
      />
    </div>
  );
}
